import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Button,
    FormControlLabel,
    FormGroup,
    Grid,
    LinearProgress,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { BarraCadastro } from 'components';
import { usePopup } from 'contexts';
import { Base } from 'layout';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { CategoriesService, UsersService } from 'services';
import { boolean as YupBoolean, object as YupObject, ref as YupRef, string as YupString } from 'yup';

const sanitizeCpf = (value) =>
    String(value ?? '')
        .replace(/\D/g, '')
        .slice(0, 11);

const formatCpf = (value) => {
    const digits = sanitizeCpf(value);

    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    }
    if (digits.length <= 9) {
        return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    }

    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const UsersDetalhe = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');

    const [isActive, setIsActive] = useState(true);
    const [categories, setCategories] = useState([]);
    const [userCategoryIds, setUserCategoryIds] = useState([]);

    const [createPopup, closePopup] = usePopup();
    const { id = 'new' } = useParams();
    const isSaveReturnRef = useRef(false);

    const schema = YupObject().shape({
        name: YupString().required(),
        email: YupString().email().required(),
        cpf: YupString()
            .transform((value, originalValue) => {
                const digits = sanitizeCpf(originalValue);
                return digits.length === 0 ? undefined : digits;
            })
            .notRequired()
            .test('cpf', 'O campo CPF deve ter 11 números', (value) => !value || value.length === 11),
        is_admin: YupBoolean().required(),
        senha: YupString()
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .notRequired()
            .min(8),
        confirma_senha: YupString()
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .when('senha', {
                is: (value) => !!value,
                then: (schema) => schema.required('Confirme a senha').min(8),
                otherwise: (schema) => schema.notRequired()
            })
            .oneOf([YupRef('senha')], 'Senhas não conferem')
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            cpf: '',
            is_admin: false,
            senha: '',
            confirma_senha: ''
        },
        resolver: yupResolver(schema),
        mode: 'onChange',
        reValidateMode: 'onChange'
    });

    useEffect(() => {
        setLoading(true);
        CategoriesService.getAll().then((result) => {
            setLoading(false);
            if (result instanceof Error) {
                console.log(result.message);
                setCategories([]);
            } else {
                setCategories(result || []);
            }
        });
    }, [id]);

    useEffect(() => {
        if (id !== 'new') {
            setLoading(true);
            setUserCategoryIds([]);
            UsersService.getById(Number(id)).then((result) => {
                setLoading(false);
                if (result instanceof Error) {
                    // Dialog
                    navigate('/users');
                } else {
                    const categorias = result.categorias || [];
                    setName(result.name || '');
                    setIsActive(result.is_active ?? true);
                    setUserCategoryIds(categorias.map((categoria) => categoria.id));
                    reset({
                        name: result.name ?? '',
                        email: result.email ?? '',
                        cpf: result.cpf ?? '',
                        is_admin: !!result.is_admin,
                        senha: '',
                        confirma_senha: '',
                        ...categorias.reduce((values, categoria) => {
                            values[categoria.id.toString()] = true;
                            return values;
                        }, {})
                    });
                }
            });
        } else {
            reset({
                name: '',
                email: '',
                cpf: '',
                is_admin: false,
                senha: '',
                confirma_senha: ''
            });
            setIsActive(true);
            setUserCategoryIds([]);
        }
    }, [id, navigate, reset]);

    const buildPayload = (input) => {
        const categorias = categories
            .filter((categoria) => Boolean(input[categoria.id.toString()]))
            .map((categoria) => Number(categoria.id));

        const senha = typeof input.senha === 'string' ? input.senha : '';

        return {
            name: input.name,
            email: input.email,
            cpf: input.cpf,
            is_active: isActive,
            is_admin: !!input.is_admin,
            categorias,
            ...(senha.length > 0 ? { password: senha } : {})
        };
    };

    const handleSave = async (input) => {
        setLoading(true);
        const payload = buildPayload(input);

        if (id === 'new') {
            const result = await UsersService.create(payload);
            setLoading(false);
            if (result instanceof Error) {
                // Dialog
            } else {
                handlePopupOkSave();
                if (isSaveReturnRef.current) {
                    navigate('/users');
                } else {
                    const user_id = result?.id || result;
                    navigate(`/users/detalhe/${user_id}`);
                }
            }
        } else {
            const result = await UsersService.updateById(Number(id), payload);
            setLoading(false);
            if (result instanceof Error) {
                // Dialog
            } else {
                handlePopupOkSave();
                if (isSaveReturnRef.current) {
                    navigate('/users');
                }
            }
        }
    };

    const save = () => {
        isSaveReturnRef.current = false;
        handleSubmit(handleSave)();
    };

    const saveReturn = () => {
        isSaveReturnRef.current = true;
        handleSubmit(handleSave)();
    };

    const handlePopupConfirmDelete = (id) => {
        createPopup({
            title: 'Excluir?',
            content: 'Você realmente deseja excluir o usuário?',
            onClose: closePopup,
            actions: (
                <Fragment>
                    <Button
                        onClick={() => {
                            closePopup();
                        }}
                        autoFocus>
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => {
                            handleDelete(id);
                            closePopup();
                        }}>
                        Excluir
                    </Button>
                </Fragment>
            )
        });
    };

    const handlePopupOkDelete = () => {
        createPopup({
            title: 'Excluido',
            content: 'Usuario excluido com sucesso!',
            actions: (
                <Fragment>
                    <Button
                        onClick={() => {
                            closePopup();
                        }}
                        autoFocus>
                        Fechar
                    </Button>
                </Fragment>
            )
        });
    };

    const handlePopupOkSave = () => {
        createPopup({
            title: 'Salvo',
            content: 'Usuario salvo com sucesso!',
            actions: (
                <Fragment>
                    <Button
                        onClick={() => {
                            closePopup();
                        }}
                        autoFocus>
                        Fechar
                    </Button>
                </Fragment>
            )
        });
    };

    const handleDelete = (id) => {
        UsersService.deleteById(id).then((result) => {
            if (result instanceof Error) {
                console.log(result.message);
            } else {
                handlePopupOkDelete();
                navigate('/users');
            }
        });
    };

    return (
        <Base
            titulo={id === 'new' ? 'Novo Usuario' : `Usuario ${name}`}
            subTitulo='Gerencie aqui as informações do usuário no sistema.'
            barra={
                <BarraCadastro
                    onClickSave={save}
                    showSaveReturn
                    onClickSaveReturn={saveReturn}
                    onClickNew={() => {
                        navigate('/users/detalhe/new');
                    }}
                    textNew='Novo'
                    showNew={id !== 'new'}
                    onClickDelete={() => {
                        handlePopupConfirmDelete(Number(id));
                    }}
                    showDelete={id !== 'new'}
                    onClickReturn={() => {
                        navigate('/users');
                    }}
                />
            }>
            <Box
                component='form'
                onSubmit={handleSubmit(handleSave)}>
                <Box
                    component={Paper}
                    elevation={0}
                    variant='outlined'
                    marginY={2}
                    marginRight={2}
                    padding={2}>
                    <Typography
                        variant='h6'
                        marginBottom={2}
                        gutterBottom>
                        Dados do Usuário
                    </Typography>
                    <Grid
                        container
                        direction='column'
                        paddingLeft={2}
                        spacing={2}>
                        {loading && (
                            <Grid
                                item
                                xs={12}>
                                <LinearProgress variant='indeterminate' />
                            </Grid>
                        )}

                        <Grid
                            container
                            item
                            direction='row'
                            spacing={2}>
                            <Grid
                                item
                                xs={12}
                                md={6}>
                                <Controller
                                    name='name'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='text'
                                            size='small'
                                            label='Nome'
                                            disabled={loading || isSubmitting}
                                            fullWidth
                                            value={field.value ?? ''}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setName(e.target.value);
                                            }}
                                            error={!!errors.name}
                                            helperText={errors.name?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}>
                                <Controller
                                    name='email'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='email'
                                            size='small'
                                            label='Email'
                                            disabled={loading || isSubmitting}
                                            fullWidth
                                            value={field.value ?? ''}
                                            error={!!errors.email}
                                            helperText={errors.email?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}>
                                <Controller
                                    name='cpf'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='text'
                                            size='small'
                                            label='CPF'
                                            disabled={loading || isSubmitting}
                                            fullWidth
                                            value={formatCpf(field.value)}
                                            onChange={(event) => {
                                                const digits = sanitizeCpf(event.target.value);
                                                field.onChange(digits);
                                            }}
                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                            error={!!errors.cpf}
                                            helperText={errors.cpf?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}>
                                <Controller
                                    name='is_admin'
                                    control={control}
                                    render={({ field }) => (
                                        <FormGroup>
                                            <FormControlLabel
                                                label='Administrador'
                                                control={
                                                    <Switch
                                                        {...field}
                                                        checked={!!field.value}
                                                        onChange={(event, checked) => field.onChange(checked)}
                                                        onBlur={field.onBlur}
                                                        disabled={loading || isSubmitting}
                                                    />
                                                }
                                            />
                                        </FormGroup>
                                    )}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}>
                                <Controller
                                    name='senha'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='password'
                                            size='small'
                                            label='Senha'
                                            disabled={loading || isSubmitting}
                                            fullWidth
                                            value={field.value ?? ''}
                                            error={!!errors.senha}
                                            helperText={errors.senha?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}>
                                <Controller
                                    name='confirma_senha'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='password'
                                            size='small'
                                            label='Confirma Senha'
                                            disabled={loading || isSubmitting}
                                            fullWidth
                                            value={field.value ?? ''}
                                            error={!!errors.confirma_senha}
                                            helperText={errors.confirma_senha?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Typography
                        variant='h6'
                        marginTop={4}
                        marginBottom={2}
                        gutterBottom>
                        Operações
                    </Typography>
                    <Box paddingLeft={2}>
                        {loading && <LinearProgress variant='indeterminate' />}
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            variant='outlined'>
                            <Table size='medium'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Referência</TableCell>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Descrição</TableCell>
                                        <TableCell>Ativo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categories.map((categoria) => {
                                        const is_active = userCategoryIds.includes(categoria.id);
                                        return (
                                            <TableRow key={categoria.id}>
                                                <TableCell>{categoria.reference_code}</TableCell>
                                                <TableCell>{categoria.name}</TableCell>
                                                <TableCell>{categoria.description}</TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={categoria.id.toString()}
                                                        control={control}
                                                        defaultValue={is_active}
                                                        render={({ field }) => (
                                                            <FormGroup>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            {...field}
                                                                            checked={!!field.value}
                                                                            size={'small'}
                                                                            onChange={(event, checked) =>
                                                                                field.onChange(checked)
                                                                            }
                                                                            onBlur={field.onBlur}
                                                                            disabled={loading || isSubmitting}
                                                                        />
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        )}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Box>
        </Base>
    );
};
